import React from 'react';
import Icon from '../../../components/AppIcon';

const ProgressIndicator = ({ currentStep = 1, totalSteps = 4 }) => {
  const steps = [
    {
      id: 1,
      title: 'Personal Information',
      description: 'Basic details and contact information',
      icon: 'User',
      status: currentStep > 1 ? 'completed' : currentStep === 1 ? 'current' : 'pending'
    },
    {
      id: 2,
      title: 'Document Upload',
      description: 'Identity verification documents',
      icon: 'Upload',
      status: currentStep > 2 ? 'completed' : currentStep === 2 ? 'current' : 'pending'
    },
    {
      id: 3,
      title: 'Verification Review',
      description: 'Document processing and approval',
      icon: 'FileCheck',
      status: currentStep > 3 ? 'completed' : currentStep === 3 ? 'current' : 'pending'
    },
    {
      id: 4,
      title: 'Account Activation',
      description: 'Complete setup and start transferring',
      icon: 'CheckCircle',
      status: currentStep > 4 ? 'completed' : currentStep === 4 ? 'current' : 'pending'
    }
  ];

  const getStepClasses = (status) => {
    switch (status) {
      case 'completed':
        return {
          container: 'bg-success text-success-foreground',
          icon: 'text-success-foreground',
          title: 'text-success font-medium',
          description: 'text-success/80'
        };
      case 'current':
        return {
          container: 'bg-primary text-primary-foreground',
          icon: 'text-primary-foreground',
          title: 'text-primary font-medium',
          description: 'text-primary/80'
        };
      default:
        return {
          container: 'bg-muted text-muted-foreground',
          icon: 'text-muted-foreground',
          title: 'text-muted-foreground',
          description: 'text-muted-foreground/60'
        };
    }
  };

  const estimatedTime = Math.max(0, (totalSteps - currentStep + 1) * 3);

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold text-foreground">Account Registration</h2>
        <p className="text-sm text-muted-foreground">
          Complete your KYC verification to start sending money internationally
        </p>
      </div>
      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Progress</span>
          <span className="font-medium text-foreground">
            {currentStep} of {totalSteps} steps
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      </div>
      {/* Steps List */}
      <div className="space-y-3">
        {steps?.map((step, index) => {
          const classes = getStepClasses(step?.status);
          const isConnected = index < steps?.length - 1;
          
          return (
            <div key={step?.id} className="relative">
              <div className="flex items-start space-x-3">
                {/* Step Icon */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${classes?.container}`}>
                  {step?.status === 'completed' ? (
                    <Icon name="Check" size={16} className={classes?.icon} />
                  ) : (
                    <Icon name={step?.icon} size={16} className={classes?.icon} />
                  )}
                </div>

                {/* Step Content */}
                <div className="flex-1 min-w-0">
                  <h4 className={`text-sm font-medium ${classes?.title}`}>
                    {step?.title}
                  </h4>
                  <p className={`text-xs ${classes?.description} mt-1`}>
                    {step?.description}
                  </p>
                </div>

                {/* Current Step Indicator */}
                {step?.status === 'current' && (
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                    <span className="text-xs text-primary font-medium">Current</span>
                  </div>
                )}
              </div>
              {/* Connector Line */}
              {isConnected && (
                <div className="ml-4 mt-2 mb-1">
                  <div className={`w-px h-4 ${
                    step?.status === 'completed' ? 'bg-success' : 'bg-border'
                  }`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Estimated Time */}
      {estimatedTime > 0 && (
        <div className="bg-muted rounded-lg p-4 text-center">
          <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Clock" size={16} />
            <span>Estimated time to complete: {estimatedTime} minutes</span>
          </div>
        </div>
      )}
      {/* Trust Signals */}
      <div className="border-t border-border pt-4 space-y-3">
        <h4 className="text-sm font-medium text-foreground text-center">
          Your Information is Secure
        </h4>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Shield" size={14} />
            <span>256-bit SSL Encryption</span>
          </div>
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Lock" size={14} />
            <span>GDPR Compliant</span>
          </div>
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Award" size={14} />
            <span>FCA Regulated</span>
          </div>
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="CheckCircle" size={14} />
            <span>PCI DSS Certified</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressIndicator;